import { ChatIdentity } from "../../../gateway/ChatIdentity/ChatIdentity";
import {
    customerUsers,
    nucontersUsers
} from "../../../utils/users-roles/users-roles";

const SCRIPT_ID = "chatwoot-sdk";

const COOKIE_EXPIRADO = "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";

/** Mensagem escrita pelo contato: `message_type` 0 (incoming) ou `sender_type` "Contact". */
function ehMensagemDoContato(detail) {
    if (!detail) return true;
    return (
        detail.message_type === 0 ||
        detail.message_type === "incoming" ||
        detail.sender_type === "Contact"
    );
}

/** Falha ao buscar a assinatura: tenta de novo depois disso, sem uma chamada por navegação. */
const NOVA_TENTATIVA_ASSINATURA_MS = 60 * 1000;

/**
 * Quem pode ver o chat: cliente (contabilidade ou empresa) e usuário interno Nucont, que vê a mesma
 * tela do cliente (os papéis internos são os `INTERNAL_ROLES` do back-api). O resto da regra — rota da
 * contabilidade e suporte contratado — vale igual para os dois.
 */
const PAPEIS_DO_CHAT = [...customerUsers, ...nucontersUsers];

/** O chat existe dentro de uma contabilidade: `/contabilidade/:accountingId/...`. */
const ACCOUNTING_STATE = "nucont.accounting";

/**
 * Compartilhado entre instâncias: a contabilidade e a empresa chegam pelos resolves e o widget é
 * controlado pelo layout, cada um com o seu `new ChatwootAdapter()`.
 */
/**
 * Etiquetas da conversa aberta por uma campanha ("campanha:simples-teto-2026"): a da campanha, que fica
 * para o CS filtrar, e `saudar-campanha`, o gatilho da automação do Chatwoot que responde na hora e
 * tira a própria etiqueta (assim a saudação sai uma vez só). As duas precisam existir no Chatwoot.
 */
export function etiquetasDaOrigem(origem) {
    if (!origem?.startsWith("campanha:")) return [];
    const campanha = origem.toLowerCase().replace(/[^a-z0-9_-]+/g, "-");
    return [campanha, "saudar-campanha"];
}

const estado = {
    visivel: false,
    user: null,
    sessaoGravada: false,
    contabilidade: null,
    empresa: null,
    /** A contabilidade aberta contratou suporte (feature "Support"); `null` até o plano carregar. */
    suporte: null,
    /** Último envio de atributos do contato, para não repetir a cada navegação. */
    ultimoContato: null,
    /** Usuário já mandado ao widget nesta página (ver `setUser`). */
    identificadoNaPagina: null,
    /** `{ userId, hash, em }`: uma consulta por sessão; falha tenta de novo depois de um tempo. */
    identidade: null,
    /** De onde a conversa foi aberta pelo código ("campanha:simples-teto-2026"); o atendente vê. */
    origem: null
};

/**
 * O Chatwoot só aceita telefone em E.164 e recusa o contato inteiro se vier fora disso.
 * O perfil guarda "DDI:número" ou só o número; sem DDI, o número é brasileiro.
 */
export function toE164(phone) {
    if (!phone) return null;
    const [ddi, numero] = String(phone).includes(":")
        ? String(phone).split(":")
        : ["", String(phone)];
    let digits = `${ddi}${numero}`.replace(/\D/g, "");
    if (!ddi && (digits.length === 10 || digits.length === 11)) {
        digits = `55${digits}`;
    }
    return /^[1-9]\d{7,14}$/.test(digits) ? `+${digits}` : null;
}

/**
 * Chat de atendimento (Chatwoot, caixa "Atendimento").
 *
 * O SDK é carregado uma vez por página. O usuário é identificado quando o widget avisa que está
 * pronto (`chatwoot:ready`), porque `window.$chatwoot` só existe a partir daí.
 *
 * Cada conversa nova recebe os dados da sessão (contabilidade, empresa aberta, tela) como
 * atributos da conversa, para o atendente ver de onde o usuário chamou.
 */
export class ChatwootAdapter {
    constructor({
        win = globalThis.window,
        settings = publicSettings.chatwoot,
        identidade = null
    } = {}) {
        this.window = win;
        this.identidade = identidade;
        this.document = win?.document;
        this.baseUrl = settings?.baseUrl;
        this.websiteToken = settings?.websiteToken;
        this.appUrl = settings?.appUrl ?? publicSettings.url;
        this.appV2Url = settings?.appV2Url ?? publicSettings.urlV2;
    }

    isEnabled() {
        return Boolean(this.baseUrl && this.websiteToken);
    }

    static isAccountingRoute(stateName) {
        return (
            stateName === ACCOUNTING_STATE ||
            Boolean(stateName?.startsWith(`${ACCOUNTING_STATE}.`))
        );
    }

    /**
     * Cliente ou usuário interno Nucont, e só em contabilidade que contratou suporte: o interno vê o
     * chat exatamente quando um cliente daquela contabilidade veria.
     */
    podeUsar(user) {
        const papelLiberado = (user?.roles || []).some((role) =>
            PAPEIS_DO_CHAT.includes(role)
        );
        return papelLiberado && estado.suporte === true;
    }

    /**
     * @public
     * Mostra o chat nas rotas da contabilidade e esconde no resto. Chamado a cada navegação.
     */
    syncRoute(stateName, user) {
        if (ChatwootAdapter.isAccountingRoute(stateName) && this.podeUsar(user)) {
            return this.start(user);
        }
        this.hide();
        return false;
    }

    /**
     * @public
     * Chamado quando o plano da contabilidade carrega. Sem suporte contratado, o chat some na hora;
     * com suporte, aparece na próxima sincronização de rota (fim da navegação).
     */
    setSuporteContratado(contratado) {
        estado.suporte = Boolean(contratado);
        if (!estado.suporte) this.hide();
    }

    /** @public Se o chat está à mostra agora (o "Fale conosco" só aparece quando abre algo). */
    isVisivel() {
        return estado.visivel;
    }

    /**
     * @public
     * À mostra E com o widget carregado: só aí o `open()` abre de fato. Quem troca outro canal pelo
     * chat (o "Ver mais" da campanha) pergunta isto, não `isVisivel` — com o script ainda carregando
     * ou barrado por bloqueador, o botão do chat ficaria mudo.
     */
    isPronto() {
        return estado.visivel && Boolean(this.window?.$chatwoot?.hasLoaded);
    }

    /**
     * @public
     * Carrega o widget e identifica o usuário logado. Chamadas repetidas só atualizam o usuário.
     */
    start(user) {
        if (!this.isEnabled() || !user?._id || user?.profile?.isAnonimo) {
            return false;
        }
        estado.user = user;
        estado.visivel = true;
        if (this.window.$chatwoot?.hasLoaded) {
            this.window.$chatwoot.toggleBubbleVisibility("show");
            this.setUser(user);
            return true;
        }
        if (this.document.getElementById(SCRIPT_ID)) return true;

        this.window.chatwootSettings = {
            locale: "pt_BR",
            position: "right",
            type: "standard"
        };
        this.window.addEventListener("chatwoot:ready", () => {
            if (!estado.visivel) {
                this.window.$chatwoot.toggleBubbleVisibility("hide");
                return;
            }
            this.setUser(estado.user);
        });
        // O retrato vai na primeira mensagem do contato a cada carregamento de página: aí a
        // conversa já existe (inclusive depois do pré-chat) e é a que ele está usando. Só o
        // `on-start-conversation` não basta: quem continua uma conversa não passa por ele.
        this.window.addEventListener("chatwoot:on-message", (event) => {
            if (!ehMensagemDoContato(event?.detail)) return;
            this.gravarSessao();
        });
        // Conversa nova: a primeira mensagem grava de novo, já na conversa nova. Sem conversa
        // anterior, grava já (o widget guarda os atributos até a conversa existir); com uma anterior
        // ("Iniciar nova conversa" depois de resolvida), não: o widget ainda aponta para a antiga e
        // a gravação sobrescreveria o histórico dela.
        this.window.addEventListener("chatwoot:on-start-conversation", (event) => {
            estado.sessaoGravada = false;
            if (event?.detail?.hasConversation) return;
            this.gravarSessao();
            estado.sessaoGravada = false;
        });
        // O widget abre sozinho com mensagem não lida, mesmo com a bolha escondida.
        // A origem vale só para a conversa aberta por ela: se a pessoa escreveu, já foi gravada;
        // se fechou sem escrever, a próxima conversa (pela bolha, mais tarde) não é da campanha.
        this.window.addEventListener("chatwoot:closed", () => {
            estado.origem = null;
        });
        this.window.addEventListener("chatwoot:opened", () => {
            if (!estado.visivel) this.window.$chatwoot?.toggle("close");
        });

        const script = this.document.createElement("script");
        script.id = SCRIPT_ID;
        script.async = true;
        script.src = `${this.baseUrl}/packs/js/sdk.js`;
        script.onload = () =>
            this.window.chatwootSDK.run({
                websiteToken: this.websiteToken,
                baseUrl: this.baseUrl
            });
        this.document.body.appendChild(script);
        return true;
    }

    async setUser(user) {
        if (!this.window.$chatwoot || !user?._id) return;
        const identifierHash = await this.assinatura(user._id);
        // Logout ou troca de usuário enquanto a assinatura vinha.
        const chatwoot = this.window.$chatwoot;
        if (!chatwoot || estado.user?._id !== user._id) return;
        const phone = toE164(user.profile?.whatsapp);
        const email = this.emailDoUsuario(user);
        // A contabilidade aberta, não a do perfil: usuário com mais de uma vê nome e link da mesma.
        const accountingId =
            estado.contabilidade?._id || user.profile?.accountingId;
        const contato = this.semVazios({
            contabilidade_id: accountingId,
            contabilidade_nome: estado.contabilidade?.name,
            contabilidade_link: this.linkContabilidade(accountingId),
            carteira_consultiva_link: this.linkCarteiraConsultiva(accountingId),
            usuario_roles: this.tipoUsuario(user)
        });
        try {
            // O SDK só manda o usuário ao widget se o cookie `cw_user_<token>` não tiver o mesmo
            // retrato. Sobra de outra sessão (o pro-v2 no mesmo domínio, um contato que o widget
            // recriou) faria o contato ficar anônimo: na primeira vez da página, o cookie sai.
            if (estado.identificadoNaPagina !== user._id) {
                this.apagarCookieDoUsuario();
                estado.identificadoNaPagina = user._id;
            }
            // O SDK recusa contato sem nome, e-mail ou avatar.
            chatwoot.setUser(user._id, {
                name: user.profile?.name || email || user.username || "Usuário Nucont",
                ...(email && { email }),
                ...(phone && { phone_number: phone }),
                ...(identifierHash && { identifier_hash: identifierHash })
            });
            // Cada envio é uma requisição ao Chatwoot; navegar não muda o contato.
            const enviado = JSON.stringify([user._id, contato]);
            if (estado.ultimoContato === enviado) return;
            chatwoot.setCustomAttributes(contato);
            estado.ultimoContato = enviado;
        } catch (err) {
            console.warn("Chatwoot: usuário não identificado", err);
        }
    }

    /**
     * Assinatura HMAC do usuário (validação de identidade do Chatwoot), feita no `users-management`
     * porque o segredo da caixa não pode vir para o navegador. Sem ela o chat continua funcionando
     * enquanto a caixa não exigir a validação; com a validação ligada, o contato fica anônimo.
     */
    async assinatura(userId) {
        const guardada = estado.identidade;
        if (
            guardada?.userId === userId &&
            (guardada.hash ||
                Date.now() - guardada.em < NOVA_TENTATIVA_ASSINATURA_MS)
        ) {
            return guardada.hash;
        }
        let hash = null;
        try {
            const gateway = this.identidade || new ChatIdentity();
            const response = await gateway.chatIdentity();
            // O back assina quem está no token; se não for este usuário, a assinatura não serve.
            if (response?.identifier === userId) hash = response.identifierHash || null;
        } catch (err) {
            console.warn("Chatwoot: identidade sem assinatura", err);
        }
        estado.identidade = { userId, hash, em: Date.now() };
        return hash;
    }

    /**
     * @public
     * Contabilidade aberta, como veio do `accounting/get-by-id` (documento inteiro).
     * Fica só em memória: MRR não pode ir para o localStorage, que o Mixpanel lê inteiro.
     */
    setContabilidade(accounting) {
        estado.contabilidade = accounting?._id ? accounting : null;
        if (this.window?.$chatwoot?.hasLoaded && estado.visivel) {
            this.setUser(estado.user);
        }
    }

    /**
     * @public
     * Empresa aberta, como veio do resolve de `nucont.accounting.company`.
     */
    setEmpresa(company) {
        estado.empresa = company?._id ? company : null;
    }

    /**
     * Grava o retrato do momento em que o usuário escreveu: uma vez por carregamento de página e
     * de novo a cada conversa nova.
     */
    gravarSessao() {
        const chatwoot = this.window.$chatwoot;
        const user = estado.user;
        if (!chatwoot || !user || estado.sessaoGravada) return;
        estado.sessaoGravada = true;

        const accounting = estado.contabilidade;
        const accountingId = accounting?._id || user.profile?.accountingId;
        const company = this.empresaNaTela();
        const dominio = accounting?.integrationsHealth?.dominio;

        chatwoot.setConversationCustomAttributes(
            this.semVazios({
                contabilidade_id: accountingId,
                contabilidade_nome: accounting?.name,
                contabilidade_link: this.linkContabilidade(accountingId),
                carteira_consultiva_link:
                    this.linkCarteiraConsultiva(accountingId),
                contabilidade_plano: accounting?.plan?.name,
                contabilidade_mrr: accounting?.superLogica?.mrr,
                ...(accounting && {
                    dominio_integrado: dominio?.companies?.connected
                        ? "sim"
                        : "não"
                }),
                dominio_empresas_conectadas: dominio?.companies?.connected,
                dominio_ultimo_dado: this.dia(dominio?.lastDataAt),
                empresa_id: company?._id,
                empresa_nome: company?.name,
                empresa_cnpj: company?.document,
                empresa_link: company
                    ? `${this.linkContabilidade(accountingId)}/empresa/${company._id}`
                    : null,
                usuario_roles: this.tipoUsuario(user),
                usuario_email: this.emailDoUsuario(user),
                usuario_telefone: user.profile?.whatsapp,
                pagina: this.paginaAtual(),
                origem: estado.origem
            })
        );
        // A etiqueta só pega com a conversa criada: a primeira mensagem do contato grava de novo.
        etiquetasDaOrigem(estado.origem).forEach((etiqueta) => chatwoot.setLabel?.(etiqueta));
    }

    /**
     * Sem a query: rotas como a do showtime recebem `liveToken`, que autentica como o usuário.
     */
    paginaAtual() {
        const location = this.window.location;
        if (!location?.pathname) return null;
        return `${location.origin || ""}${location.pathname}`;
    }

    /** A última empresa aberta só vale se o usuário ainda estiver dentro dela. */
    empresaNaTela() {
        const company = estado.empresa;
        const path = this.window.location?.pathname || "";
        return company && path.includes(`/empresa/${company._id}`)
            ? company
            : null;
    }

    linkContabilidade(accountingId) {
        if (!accountingId || !this.appUrl) return null;
        return `${this.appUrl}/contabilidade/${accountingId}`;
    }

    /**
     * Análise de ciclo da carteira consultiva, no pro-v2. Sem o `rwt` que o `RedirectV2Service`
     * acrescenta: o link vai para o Chatwoot e não pode levar o refresh token do usuário.
     */
    linkCarteiraConsultiva(accountingId) {
        if (!accountingId || !this.appV2Url) return null;
        return `${this.appV2Url}/contabilidade/${accountingId}/carteira-consultiva/analise-ciclo?accId=${accountingId}`;
    }

    dia(date) {
        if (!date) return null;
        const parsed = new Date(date);
        return isNaN(parsed) ? null : parsed.toISOString().slice(0, 10);
    }

    /** O login é o e-mail: o app guarda em `username`; `emails[0]` só existe no modelo antigo. */
    emailDoUsuario(user) {
        const email = user.emails?.[0]?.address || user.username;
        return email?.includes("@") ? email : null;
    }

    tipoUsuario(user) {
        return user.roles?.join(", ") || user.profile?.type || null;
    }

    /** O Chatwoot exibe atributo vazio como chave sem valor; melhor não mandar. */
    semVazios(attributes) {
        return Object.fromEntries(
            Object.entries(attributes).filter(
                ([, value]) => value !== null && value !== undefined && value !== ""
            )
        );
    }

    /**
     * @public
     * Abre a janela de conversa (botão "Fale conosco"). `origem` marca de onde veio, para o
     * atendente saber o assunto antes da primeira mensagem, e vale até a janela fechar. Abrir sem
     * ela só faz a próxima gravação sair sem `origem`; o que já foi gravado na conversa fica.
     */
    open(origem = null) {
        if (!estado.visivel) return;
        // Origem nova numa conversa já registrada: a próxima mensagem grava de novo, com ela.
        if (origem && origem !== estado.origem) estado.sessaoGravada = false;
        estado.origem = origem;
        this.window?.$chatwoot?.toggle("open");
    }

    /**
     * @public
     * Esconde o chat (bolha e janela) fora das rotas da contabilidade; a sessão do contato continua.
     */
    hide() {
        estado.visivel = false;
        const chatwoot = this.window?.$chatwoot;
        if (!chatwoot?.hasLoaded) return;
        // Esconder a bolha não fecha a janela de conversa que estiver aberta.
        chatwoot.toggle("close");
        chatwoot.toggleBubbleVisibility("hide");
    }

    /**
     * @public
     * Encerra a sessão do contato no widget (logout), para o próximo usuário não herdar a conversa.
     */
    reset() {
        estado.visivel = false;
        estado.user = null;
        estado.sessaoGravada = false;
        estado.contabilidade = null;
        estado.empresa = null;
        estado.suporte = null;
        estado.ultimoContato = null;
        estado.identificadoNaPagina = null;
        estado.identidade = null;
        estado.origem = null;
        this.apagarCookiesDoWidget();
        const chatwoot = this.window?.$chatwoot;
        if (!chatwoot?.hasLoaded) return;
        chatwoot.reset();
        chatwoot.toggleBubbleVisibility("hide");
    }

    /**
     * A sessão do contato fica em cookies do domínio do app (365 dias), lidos quando o widget
     * monta. O `$chatwoot.reset()` só existe com o SDK carregado; sair de uma tela sem chat não
     * pode deixar a conversa para o próximo usuário do mesmo navegador.
     */
    apagarCookiesDoWidget() {
        if (!this.document || !this.websiteToken) return;
        this.document.cookie = `cw_conversation${COOKIE_EXPIRADO}`;
        this.apagarCookieDoUsuario();
    }

    apagarCookieDoUsuario() {
        if (!this.document || !this.websiteToken) return;
        this.document.cookie = `cw_user_${this.websiteToken}${COOKIE_EXPIRADO}`;
    }
}
